/* External dependencies */
import React, { useCallback } from 'react';
import { Dispatch } from 'redux';
import { Dropdown } from 'react-bootstrap';
import { connect } from 'react-redux';
import get from 'lodash/get';

/* Internal dependencies */
import Button from 'src/button/Button';
import Icon, { Icons } from 'src/icon/Icon';
import { ParticipantStatus } from 'src/types/Participant';
import EventType, { Event } from 'src/types/Event';
import { respondToCreatorInvitation } from 'src/api/events';
import { updateCommunity } from 'src/store/ducks/communities';
import { getEvent, updateEvent } from 'src/store/ducks/events';
import Community from 'src/types/Community';
import { CurrentUserState, getCurrentUser, updateCurrentUser } from 'src/store/ducks/currentUser';
import User from 'src/types/User';
import { ApplicationState } from 'src/store';

type OwnProps = {
  creator: any;
  event: any;
  iconSize?: number;
  showIcon?: boolean;
  fontSize?: string;
  width?: number | string;
  onAccept?: (event: Event) => void;
  onDecline?: (event: Event) => void;
};

type StateProps = {
  currentUser: CurrentUserState['user'];
};

type DispatchProps = {
  updateCommunity(community: Community): void;
  updateEvent(event: Event): void;
  updateCurrentUser(user: User): void;
};

type Props = OwnProps & StateProps & DispatchProps;

const RespondToHostInvitationButton: React.FC<Props> = ({
  creator, event,
  updateCommunity,
  updateEvent,
  updateCurrentUser,
  onAccept,
  onDecline,
  currentUser,
}) => {
  const creatorId = get(creator, 'id', '');
  const creatorItemType = get(creator, 'type'); // User or Community
  const createdEvents: EventType[] = Boolean(creatorItemType === 'community') ? get(creator, 'events.items', []) : get(creator, 'createdEvents', []);
  const eventId = get(event, 'id');
  const eventCreatorType = get(event, 'creatorType');
  const eventCreatorStatus = get(event, 'creatorStatus');

  const createHandleAcceptHostInvitation = useCallback((event: EventType) => async () => {
    const eventIndex = createdEvents.findIndex(({ id }) => id === eventId);

    if (creatorItemType === 'community') {
      updateCommunity({
        ...creator,
        events: {
          ...get(creator, 'events'),
          items: [
            ...createdEvents.slice(0, eventIndex),
            {
              ...event,
              creatorStatus: ParticipantStatus.confirmed,
            },
            ...createdEvents.slice(eventIndex + 1),
          ],
        },
      });
    } else if (creatorItemType === 'user') {
      updateCurrentUser({
          ...currentUser,
          createdEvents: [
              ...createdEvents.slice(0, eventIndex),
              {
                  ...event,
                  creatorStatus: ParticipantStatus.confirmed,
              },
              ...createdEvents.slice(eventIndex + 1),
          ],
      } as User);
  }

    updateEvent({
      ...event,
      creatorStatus: ParticipantStatus.confirmed,
    });

    onAccept && onAccept(event);

    await respondToCreatorInvitation(eventId, { id: creatorId, status: ParticipantStatus.confirmed });
  }, [createdEvents]);

  const createHandleDeclineHostInvitation = useCallback((event: EventType) => async () => {
    const eventId = get(event, 'id');
    const eventIndex = createdEvents.findIndex(({ id }) => id === eventId);

    if (creatorItemType === 'community') {
      updateCommunity({
        ...creator,
        events: {
          ...get(creator, 'events'),
          items: [
            ...createdEvents.slice(0, eventIndex),
            {
              ...event,
              creatorStatus: ParticipantStatus.denied,
            },
            ...createdEvents.slice(eventIndex + 1),
          ],
        },
      });
    } else if (creatorItemType === 'user') {
      updateCurrentUser({
          ...currentUser,
          createdEvents: [
              ...createdEvents.slice(0, eventIndex),
              {
                  ...event,
                  creatorStatus: ParticipantStatus.denied,
              },
              ...createdEvents.slice(eventIndex + 1),
          ],
      } as User);
    }

    updateEvent({
      ...event,
      creatorStatus: ParticipantStatus.denied,
    });

    onDecline && onDecline(event);
    
    await respondToCreatorInvitation(eventId, { id: creatorId, status: ParticipantStatus.denied });
  }, [createdEvents]);

  if (Boolean(eventCreatorStatus !== ParticipantStatus.invited)) {
    return null;
  }

  return (
    <Button
      className="btn btn-secondary text-dark d-flex justify-content-center align-items-center"
      dropdownItems={[
        <Dropdown.Item disabled={true} className="d-flex justify-content-center align-items-center" key="title" onClick={() => {}}>
          {Boolean(eventCreatorType === 'owner') ? 'Respond to ownership transfer' : 'Respond to co-host invitation'}
        </Dropdown.Item>,
        <Dropdown.Item className="d-flex justify-content-center align-items-center" key="accept" onClick={createHandleAcceptHostInvitation(event) as any}>
          <Icon name={Icons.check} className="mr-2" size={22} />
          {Boolean(eventCreatorType === 'owner') ? 'Accept transfer' : 'Accept invitation'}
        </Dropdown.Item>,
        <Dropdown.Item className="d-flex justify-content-center align-items-center text-danger" key="decline" onClick={createHandleDeclineHostInvitation(event) as any}>
          <Icon name={Icons.cross} className="bm-Icon--danger mr-2" size={22} />
          {Boolean(eventCreatorType === 'owner') ? 'Decline transfer' : 'Decline invitation'}
        </Dropdown.Item>,
      ]}
      style={{ borderRadius: 100 }}
    >
      Respond
      <Icon name={Icons.chevronDown} className="ml-1" size={18} />
    </Button>
  );
};

const mapStateToProps = (state: ApplicationState, { event }: OwnProps) => {
  const eventId = get(event, 'id');

  return {
      currentUser: getCurrentUser(state),
      event: getEvent(state, eventId) || event,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateCommunity: (community: Community) => { dispatch(updateCommunity(community)); },
  updateEvent: (event: Event) => { dispatch(updateEvent(event)); },
  updateCurrentUser: (user: User) => { dispatch(updateCurrentUser(user)); },
});

export default connect(mapStateToProps, mapDispatchToProps)(RespondToHostInvitationButton);